import { Tab, Tabs } from "react-bootstrap";
import { useFormContext, useWatch } from "react-hook-form";
import { Table } from "stories";
import { currencyStyle, parseCurrency } from "utils/customHooks/currencyFormat";
import { getColumnTotal } from "../../equityBreakdown";
// {
// 	unit_type: "",
// 	total_units:"",
// 	total_area:"",
// 	total:"",
// 	avg_price
// }

const tableHeader = (projectPurpose) => [
  {
    id: "unit_type",
    title: "Unit Type",
    align: "left",
  },
  {
    id: "total_units",
    title: "Total Units",
    align: "right",
  },
  {
    id: "total_area",
    title: "Total Area (SF)",
    align: "right",
  },
  {
    id: "total",
    title: projectPurpose === "sale" ? "Total Market Value" : "Total Revenue",
    align: "right",
  },
  {
    id: "avg_price",
    title: projectPurpose === "sale" ? "Avg Price PSF" : "Avg Revenue PSF",
    align: "right",
  },
];

export const generateTitle = (key) => {
  const keySections = key.split(">");
  if (keySections[1] === "other")
    return `${keySections[0]} ${keySections[1]} (${
      keySections[2] === "strata" ? "S" : "NS"
    })`;
  else return `${keySections[1]} (${keySections[2] === "strata" ? "S" : "NS"})`;
};
const isNaNZero = (data) => {
  if (data === Infinity || data === -Infinity) return 0;
  return !isNaN(data) ? Number(data.toFixed(2)) : 0;
};
const genTotal = (rows) => {
  let totalArea = 0;
  for (const row of rows) {
    if (row.count && row.avg_sq_ft)
      totalArea += Number(parseFloat(row.count) * parseFloat(row.avg_sq_ft));
  }
  const data = {
    total_units: currencyStyle(getColumnTotal(rows, "count")),
    total_area: 0,
    total: `$ ${currencyStyle(getColumnTotal(rows, "total_market_value"))}`,
    avg_price: `$ ${currencyStyle(
      isNaNZero(
        Number(
          (getColumnTotal(rows, "total_market_value") / totalArea).toFixed(2)
        )
      )
    )}`,
  };
  data.total_area = currencyStyle(totalArea);
  return data;
};
const generateUnitMix = (assets) => {
  const rows = [];

  for (const key of Object.keys(assets)) {
    const data = genTotal(assets[key]);
    data.uid = key;
    data.unit_type = generateTitle(key);
    data.props = {
      unit_type: { className: "capitalize" },
    };
    rows.push(data);
  }

  return rows;
};

const generateFooterData = (rows) => {
  return [
    {
      uid: "unit-mix-sub-total",
      unit_type: "Unit Mix Sub-Total",
      total_units: currencyStyle(getColumnTotal(rows, "total_units")),
      total_area: currencyStyle(getColumnTotal(rows, "total_area")),
      total: `$ ${currencyStyle(getColumnTotal(rows, "total"))}`,
      avg_price: `$ ${currencyStyle(
        isNaNZero(
          getColumnTotal(rows, "total") / getColumnTotal(rows, "total_area")
        )
      )}`,
    },
  ];
};

const generatePSFooterData = (rows) => {
  return [
    {
      uid: "other-sub-total-x",
      unit_type: "Other Sub-Total",
      total_units: currencyStyle(getColumnTotal(rows, "total_units")),
      total_area: currencyStyle(getColumnTotal(rows, "total_area")),
      total: `$ ${currencyStyle(getColumnTotal(rows, "total"))}`,
      avg_price: `$ ${currencyStyle(
        isNaNZero(
          getColumnTotal(rows, "total") / getColumnTotal(rows, "total_area")
        )
      )}`,
    },
  ];
};

export const SummerySection = ({ hasParkingStorage }) => {
  const form = useFormContext();
  const projectPurpose = useWatch({
    control: form.control,
    name: "project_purpose",
  });
  const [assets, parkingAssets, storageAssets, saleTaxPercentage] = useWatch({
    name: ["assets", "parking_assets", "storage_assets", "sale_tax_percentage"],
    control: form.control,
  });

  const mixUnitsRows = generateUnitMix(assets);
  const mixUnitFooter = generateFooterData(mixUnitsRows);

  let otherTableRows = [];
  let parkingRow = {};
  let storageRow = {};
  let psTotal = [];
  const hasParking = hasParkingStorage.parking_assets;
  const hasStorage = hasParkingStorage.storage_assets;

  if (hasParking) {
    parkingRow = genTotal(parkingAssets);
    parkingRow.uid = "parking";
    parkingRow.unit_type = "Parking";
    otherTableRows.push(parkingRow);
  }
  if (hasStorage) {
    storageRow = genTotal(storageAssets);
    storageRow.uid = "storage";
    storageRow.unit_type = "Storage";
    otherTableRows.push(storageRow);
  }

  if (hasParking || hasStorage) {
    psTotal = generatePSFooterData(otherTableRows);
  }
  const handlePercentageChange = (_, __, saleTaxP) => {
    form.setValue("sale_tax_percentage", saleTaxP);
  };

  let totalTableRows = [];
  if (projectPurpose === "sale") {
    totalTableRows = [
      {
        uid: "total-sales-tax-x",
        unit_type: "Total Sales Tax",
        total_units: saleTaxPercentage,
        total_area: "-",
        total: () => {
          const p = saleTaxPercentage;
          if (isNaN(p)) return "$ -";
          return `$ ${currencyStyle(
            Number(
              (
                (-1 *
                  (p *
                    ((psTotal[0] ? parseCurrency(psTotal[0].total) : 0) +
                      (mixUnitFooter[0]
                        ? parseCurrency(mixUnitFooter[0].total)
                        : 0)))) /
                100
              ).toFixed(2)
            )
          )}	`;
        },
        avg_price: "-",
        props: {
          total_units: {
            editable: true,
            type: "currency",
            suffix: " %",
            prefix: "",
          },
        },
      },
    ];
  }

  let totalTableFooter = [
    {
      uid: "total-max-footer",
      unit_type: "Total",
      total_units: "",
      total_area: currencyStyle(
        getColumnTotal([mixUnitFooter[0], psTotal[0]], "total_area")
      ),
      total: `$ ${currencyStyle(
        getColumnTotal([mixUnitFooter[0], psTotal[0]], "total") +
          (totalTableRows.length && totalTableRows[0].total() !== "$ -"
            ? Number(parseCurrency(totalTableRows[0].total()).toFixed(2))
            : 0)
      )}`,
      avg_price: (row) => {
        const a = Number(parseCurrency(row.total));
        const b = Number(parseCurrency(row.total_area));

        return `$ ${currencyStyle(
          isNaNZero(Number((!isNaN(a / b) ? a / b : 0).toFixed(2)))
        )}`;
      },
    },
  ];

  return (
    <Tabs activeKey="abs">
      <Tab
        eventKey="abs"
        title="Asset Breakdown Summary"
        tabClassName="text-left pl-3"
      >
        <div className="mb-2">
          <span className="fw-bold title mb-2 d-block">Unit Mix</span>
          <Table
            sameColumnWidth
            headers={tableHeader(projectPurpose)}
            rows={mixUnitsRows}
            footer={mixUnitFooter}
          />
        </div>
        {(hasParking || hasStorage) && (
          <div className="mb-2">
            <span className="fw-bold title mb-2 d-block">Other</span>
            <Table
              sameColumnWidth
              headers={tableHeader(projectPurpose)}
              rows={otherTableRows}
              footer={psTotal}
            />
          </div>
        )}

        <Table
          hideHeader
          sameColumnWidth
          headers={tableHeader(projectPurpose)}
          rows={totalTableRows}
          footer={totalTableFooter}
          onFieldChanged={handlePercentageChange}
        />
      </Tab>
    </Tabs>
  );
};
